import Input from "../../Shared/Input";
import TextArea from "../../Shared/TextArea";
import Button from "../../Shared/Button";
import { ButtonTypes } from "../../../utils/consts";
import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { queryClient } from "../../../main";
import { CreateReview } from "../../../api/Reviews/CreateReview";

const validationSchema = Yup.object({
  name: Yup.string().required("Name is required"),
  review: Yup.string().required("Review is required"),
  rating: Yup.string()
    .required("Rating is required")
    .min(0, "Rating must be at least 0")
    .max(5, "Rating must be at most 5")
    .typeError("Rating must be a number")
    .nullable(),
});

const AddReview = ({ onSuccess }: { onSuccess?: () => void }) => {
  const [reviews, setReviews] = useState([
    { id: 1, name: "", review: "", rating: "" },
  ]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleAddReviews = () => {
    const newReview = {
      id: reviews.length + 1,
      name: "",
      review: "",
      rating: "",
    };
    setReviews([...reviews, newReview]);
  };

  const handleChange = (index: number, field: string, value: string) => {
    const updatedFaqs = reviews.map((review, idx) => {
      if (idx === index) {
        return { ...review, [field]: value };
      }
      return review;
    });
    setReviews(updatedFaqs);
  };

  const handleDeleteReview = (index: number) => {
    setReviews(reviews.filter((_, idx) => idx !== index));
  };

  const { mutate } = useMutation({
    mutationFn: CreateReview,
    onSuccess: () => {
      toast.success("Review Added successfully");
      queryClient.invalidateQueries({ queryKey: ["reviews"] });
      setIsSubmitting(true);
      onSuccess && onSuccess();
    },
    onError: (error) => {
      toast.error("Error adding Review");
      console.error(error);
    },
  });

  const handleSubmit = (event: { preventDefault: () => void }) => {
    event.preventDefault();
    if (reviews.every((review) => validationSchema.isValidSync(review))) {
      const payload = reviews.map(({ name , review, rating }) => ({
        name, 
        review,
        rating: Number(rating),
      }));
      mutate(payload);
    } else {
      reviews.forEach((review, index) => {
        const errors = validationSchema.validateSync(review, {
          abortEarly: false,
        });
        Object.entries(errors).forEach(([key, value]) => {
          toast.error(`${key} at position ${index + 1}: ${value}`);
        });
      });
    }
  };

  return (
    <>
      <div className="mb-9 flex justify-between">
        <div>
          <h1 className="font-bold text-28px mb-2.5">Reviews</h1>
          <p className="text-lightGray font-medium">
            Add a Review and Rating for each review
          </p>
        </div>
        <Button
          onClick={handleAddReviews}
          variant="primary"
          className="!bg-[#DBF0FE] !text-[#4379EE] border-[#62BDF8] border hover:border-transparent hover:text-white hover:!bg-[#62BDF8] !h-12 w-40 px-1 text-lg font-bold"
        >
          Add Review
        </Button>
      </div>
      <div className="">
        <div className="border-b border-black/20 pb-10">
          <form onSubmit={handleSubmit} className="max-w-[600px] space-y-8">
            {reviews.map((review, index) => (
              <div key={review.id}>
                <Input
                  value={review.name}
                  className="w-full"
                  inputStyles="border-opacity-15"
                  placeholder="Name"
                  name="name"
                  type="text"
                  onChange={(e) => handleChange(index, "name", e.target.value)}
                />

                <Input
                  value={review.rating}
                  className="w-full"
                  inputStyles="border-opacity-15"
                  placeholder="Rating"
                  name="rating"
                  type="text"
                  onChange={(e) =>
                    handleChange(index, "rating", e.target.value)
                  }
                />
                <TextArea
                  value={review.review}
                  name="review"
                  placeholder="Enter Review"
                  inputStyles="!h-32"
                  onChange={(e) =>
                    handleChange(index, "review", e.target.value)
                  }
                />
                <div className="flex justify-end gap-3 mt-4">
                  <Button
                    type={ButtonTypes.BUTTON}
                    onClick={() => handleDeleteReview(index)}
                    className="before:!bg-primary hover:text-white !text-base !font-bold !h-9 w-20 !border-primary !border-2 text-primary"
                    variant="transparent"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ))}
            <Button
              type={ButtonTypes.SUBMIT}
              variant="primary"
              className="!h-9 w-20 !text-base !font-bold ml-auto"
              disabled={isSubmitting}
            >
              Add
            </Button>
          </form>
        </div>
      </div>
    </>
  );
};

export default AddReview;
